import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import useAuth from "../../hooks/use-auth";
import styled from "styled-components";
import * as Yup from "yup";
import { Box, Button } from "@vinomofo/components";
import { trackEvent } from "../../utils/track-event";
import { TrackingEventName } from "../../utils/event-tracking";
import { HideShowPasswordIcon } from '@vinomofo/icons';

export const ErrorBox = styled(Box)`
  margin: 0 auto;
  display: inline;
  padding: 2px 10px;
  text-align: center;
  border: 1px solid #cf2c30;
  background-color: #cf2c30;
  color: white;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  border-radius: 12px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  font-family: 'Graphik', sans-serif;
  overflow-wrap: break-word;
  white-space: normal;

  a {
    text-align: center;
    display: inline;
  }
`;

const ModalButton = styled.button`
  font-family: "Graphik", sans-serif;
  background-color: #48A90F;
  color: white;
  border: none;
  border-radius: 50px; 
  cursor: pointer;
  font-size: 0.8em;
  transition: background-color 0.3s;
  display: inline-block; 
  padding: 15px 80px;
  &:hover {
    background-color: #77b106;
  }
  @media (max-width: 768px) {
    padding: 10px 50px;
  }
`;

const LoginInput = styled(Field)`
  font-family: "Graphik", sans-serif;
  font-size: 18px;
  width: 100%;
  max-width: 333px;
  padding: 10px;
  text-align: center;
  border-top: none;
  border-left: none;
  border-right: none;
  margin-bottom: 10px;
  &:focus {
    outline: none;
    border-bottom: 2px solid #77b106;
  }
`;

const LoginForm = React.memo(({ eventName }: { eventName: TrackingEventName }) => {
  const { loginWithCredentials } = useAuth();
  const [revealPassword, setRevealPassword] = useState(false);
  const handleToggleClick = () => {
    setRevealPassword(!revealPassword);
  };
  const baseUrl = `${process.env.GATSBY_AUTH0_DOMAIN}/login?client=${process.env.GATSBY_AUTH0_CLIENT_ID}&tenantDomain=${process.env.GATSBY_AUTH0_TENANT_DOMAIN}&audience=${encodeURIComponent(process.env.GATSBY_AUTH0_AUDIENCE)}`;

  const loginWithParameter = async (
    values: { email: string; password: string },
    setSubmitting: (isSubmitting: boolean) => void,
    setError: (msg: string | null) => void
  ) => {
    try {

      trackEvent(eventName, {
        location: location?.pathname ?? "/",
        button: "Paywall Login Button"
      });

      const delimiter = 'A8k@b#Z2!x';
      const timecode = Date.now(); // UTC timestamp in milliseconds

      const combined = `${values?.email}${delimiter}${values?.password}${delimiter}${timecode}`;
      const encoded = btoa(combined);

      loginWithCredentials({ returnTo: window.location.href }, encoded);
    } catch (error) {
      console.error("Failed to connect to the server.");
      setError("Server error. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    
    trackEvent(eventName, {
      location: location?.pathname ?? "/",
      button: "Paywall Forgot Password Button"
    });

    let redirectUrl = baseUrl + `/#/auth/login/forgot-password`;
    if (!redirectUrl.startsWith('http')) {
      redirectUrl = `https://${redirectUrl}`;
    }
    window.location.href = redirectUrl;
  };

  const handleOtherOption = () => {
    trackEvent(eventName, {
      location: location?.pathname ?? "/",
      button: "Paywall Other Option Button"
    });

    let redirectUrl = baseUrl + `/#/auth/login?returnTo=/`;
    if (!redirectUrl.startsWith('http')) {
      redirectUrl = `https://${redirectUrl}`;
    }
    window.location.href = redirectUrl;
  };

  const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email").required("Required"),
    password: Yup.string().required("Required"),
  });

  return (
    <Formik
      initialValues={{ email: "", password: "" }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting, setFieldError }) => {
        loginWithParameter(values, setSubmitting, (msg) => {
          if (msg) setFieldError("general", msg);
        });
      }}
    >
      {({ isSubmitting, errors }) => (
        <Form style={{ maxWidth: 400 }}>
          <div style={{ marginBottom: 16 }}>
            <LoginInput
              type="email"
              name="email"
              placeholder="Your email address"
              required
            />
            <ErrorMessage name="email">
              {msg => <div style={{ textAlign: "right" }}><ErrorBox>{msg}</ErrorBox></div>}
            </ErrorMessage>
          </div>
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", right: "10px", top: "10px" }}>
              <Button
                type="button"
                onClick={handleToggleClick}
                hoverColor="primary"
                color={revealPassword ? 'primary' : 'dorianGray'}
              >
                <HideShowPasswordIcon isClicked={revealPassword} width="24" height="24" />
              </Button>
            </div>

            <LoginInput
              type={revealPassword ? 'text' : 'password'}
              name="password"
              placeholder="Your password"
              required
            />
            <ErrorMessage name="password">
              {msg => <div style={{ textAlign: "right" }}><ErrorBox>{msg}</ErrorBox></div>}
            </ErrorMessage>
          </div>
          <div style={{ textAlign: "right" }}>
            <a onClick={handleForgotPassword} style={{ fontSize: "10px", textDecoration: "underline", color: "black", fontWeight: 300, cursor: "pointer" }}>FORGOT PASSWORD</a>
          </div>
          {errors.general && <p style={{ color: "red" }}>{errors.general}</p>}
          <div style={{ width: "100%", maxWidth: "333px", display: "flex", alignItems: "center", marginTop: "20px", marginBottom: "30px", fontFamily: `"Graphik", sans-serif` }}>
            <ModalButton
              type="submit"
              disabled={isSubmitting}
              style={{ padding: "10px 30px", fontSize: "18px", fontWeight: "500", flex: 1 }}
            >
              {isSubmitting ? "Logging in..." : "Log in"}
            </ModalButton>
          </div>
          <div style={{ fontSize: "12px", marginTop: "10px", display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
            <div>
              NOT A MOFO YET? <a style={{ textDecoration: "underline", color: "black" }} href="/join">SIGN UP</a>
            </div>
            <div>
              <a onClick={handleOtherOption} style={{ textDecoration: "underline", color: "black", fontWeight: 300, cursor: "pointer" }}>OTHER OPTIONS</a>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
});

export default LoginForm;
